import React, { useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { SearchableSelect } from '../ui/searchable-select';
import { cn } from '../../lib/utils';
import type { AddPlanConstraintInput, PlanConstraintType, PlanSlotInfo, StaffingPlanBoard } from './types';
import type { BoardModel } from './boardModel';
import { dayMonth, employeeName, slotHours, sortSlots, weekdayShort } from './planningUtils';

export const REASON_MAX_LENGTH = 200;
const MAX_COUNT = 99;

export const CONSTRAINT_TYPE_LABELS: Record<PlanConstraintType, string> = {
  EMPLOYEE_UNAVAILABLE: 'Pracownik niedostępny',
  EMPLOYEE_ONLY_SLOT: 'Pracownik tylko na wybranej zmianie',
  EMPLOYEE_NOT_SLOT: 'Pracownik bez wybranej zmiany',
  POSITION_HEADCOUNT_OVERRIDE: 'Inna obsada stanowiska w wybranym dniu',
};

const TYPE_HINTS: Record<PlanConstraintType, string> = {
  EMPLOYEE_UNAVAILABLE: 'W tych dniach program nie przydzieli tej osobie żadnej zmiany.',
  EMPLOYEE_ONLY_SLOT: 'W tych dniach ta osoba może dostać tylko wybraną zmianę (albo wolne).',
  EMPLOYEE_NOT_SLOT: 'W tych dniach ta osoba nie dostanie wybranej zmiany.',
  POSITION_HEADCOUNT_OVERRIDE: 'Na ten jeden dzień zastępuje wymaganą obsadę stanowiska na wybranej zmianie.',
};

const SELECT_CLASS =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60';

type Field = 'employee' | 'position' | 'slot' | 'dateFrom' | 'dateTo' | 'min' | 'target' | 'reason';

function parseCount(text: string): number | null {
  const t = text.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return n <= MAX_COUNT ? n : null;
}

const collator = new Intl.Collator('pl');

interface PlanConstraintFormProps {
  board: StaffingPlanBoard;
  model: BoardModel;
  busy: boolean;
  onSubmit: (input: Omit<AddPlanConstraintInput, 'planId'>) => Promise<boolean>;
  onCancel: () => void;
}

export const PlanConstraintForm: React.FC<PlanConstraintFormProps> = ({ board, model, busy, onSubmit, onCancel }) => {
  const [type, setType] = useState<PlanConstraintType>('EMPLOYEE_UNAVAILABLE');
  const [employeeId, setEmployeeId] = useState('');
  const [positionId, setPositionId] = useState('');
  const [slotId, setSlotId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [min, setMin] = useState('');
  const [target, setTarget] = useState('');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState<Partial<Record<Field, boolean>>>({});

  const touch = (f: Field) => setTouched((t) => (t[f] ? t : { ...t, [f]: true }));

  const isEmployeeType = type !== 'POSITION_HEADCOUNT_OVERRIDE';
  const needsSlot = type !== 'EMPLOYEE_UNAVAILABLE';
  const singleDay = type === 'POSITION_HEADCOUNT_OVERRIDE';

  const employeeOptions = useMemo(
    () =>
      [...board.employees]
        .sort((a, b) => collator.compare(a.lastName, b.lastName) || collator.compare(a.firstName, b.firstName))
        .map((e) => ({
          value: e.employeeId,
          label: `${employeeName(e)}${e.allowedSlotIds.length === 0 ? ' (brak zmianowości)' : ''}`,
        })),
    [board.employees],
  );

  const positions = useMemo(() => {
    const map = new Map<string, { id: string; label: string; slotIds: Set<string> }>();
    for (const e of board.employees) {
      if (!e.positionId) continue;
      let p = map.get(e.positionId);
      if (!p) {
        p = {
          id: e.positionId,
          label: `${e.positionName ?? 'Stanowisko'}${e.departmentName ? ` (${e.departmentName})` : ''}`,
          slotIds: new Set(),
        };
        map.set(e.positionId, p);
      }
      e.allowedSlotIds.forEach((id) => p!.slotIds.add(id));
    }
    return Array.from(map.values()).sort((a, b) => collator.compare(a.label, b.label));
  }, [board.employees]);

  const employee = employeeId ? model.employeeById.get(employeeId) : undefined;
  const position = positions.find((p) => p.id === positionId);

  const slotChoices: PlanSlotInfo[] = useMemo(() => {
    const ids = isEmployeeType ? employee?.allowedSlotIds ?? [] : Array.from(position?.slotIds ?? []);
    return sortSlots(ids.map((id) => model.slotById.get(id)).filter((s): s is PlanSlotInfo => !!s));
  }, [isEmployeeType, employee, position, model.slotById]);

  // ------------------------------------------------------------ validation
  const errors: Partial<Record<Field, string>> = {};
  if (isEmployeeType) {
    if (!employeeId) errors.employee = 'Wybierz pracownika';
    else if (needsSlot && employee && employee.allowedSlotIds.length === 0) {
      errors.employee = 'Ta osoba nie ma zmianowości, więc nie można wybrać dla niej zmiany';
    }
  } else if (!positionId) {
    errors.position = 'Wybierz stanowisko';
  } else if (slotChoices.length === 0) {
    errors.position = 'Osoby na tym stanowisku nie mają zmianowości';
  }
  if (needsSlot && !errors.employee && !errors.position) {
    if (!slotId) errors.slot = 'Wybierz zmianę';
    else if (!slotChoices.some((s) => s.id === slotId)) errors.slot = 'Ta zmiana nie jest dostępna dla wybranej osoby';
  }
  if (!dateFrom) errors.dateFrom = singleDay ? 'Wybierz dzień' : 'Wybierz pierwszy dzień';
  else if (!board.dates.includes(dateFrom)) errors.dateFrom = 'Dzień musi należeć do tygodnia planu';
  if (!singleDay && dateTo && dateFrom && dateTo < dateFrom) {
    errors.dateTo = 'Ostatni dzień nie może być wcześniejszy niż pierwszy';
  }
  if (singleDay) {
    const minN = parseCount(min);
    const targetN = parseCount(target);
    if (minN === null) errors.min = `Wpisz liczbę całkowitą od 0 do ${MAX_COUNT}`;
    if (targetN === null) errors.target = `Wpisz liczbę całkowitą od 0 do ${MAX_COUNT}`;
    else if (minN !== null && targetN < minN) errors.target = 'Docelowo nie może być mniej niż minimum';
  }
  if (reason.trim().length > REASON_MAX_LENGTH) {
    errors.reason = `Powód może mieć najwyżej ${REASON_MAX_LENGTH} znaków (teraz: ${reason.trim().length})`;
  }
  const invalid = Object.keys(errors).length > 0;

  // Empty required fields are flagged only after the user touched them;
  // format and logic errors show at once.
  const shown = (f: Field, emptyValue: boolean) => (emptyValue && !touched[f] ? undefined : errors[f]);

  const changeType = (t: PlanConstraintType) => {
    setType(t);
    setSlotId('');
    setDateTo('');
    setTouched({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (invalid || busy) {
      setTouched({ employee: true, position: true, slot: true, dateFrom: true, min: true, target: true });
      return;
    }
    const input: Omit<AddPlanConstraintInput, 'planId'> = { type, dateFrom };
    if (isEmployeeType) input.employeeId = employeeId;
    else input.positionId = positionId;
    if (needsSlot) input.shiftTimeId = slotId;
    if (!singleDay && dateTo && dateTo !== dateFrom) input.dateTo = dateTo;
    if (singleDay) {
      input.minHeadcount = parseCount(min) ?? 0;
      input.targetHeadcount = parseCount(target) ?? 0;
    }
    if (reason.trim()) input.reason = reason.trim();
    const ok = await onSubmit(input);
    if (ok) {
      setEmployeeId('');
      setPositionId('');
      setSlotId('');
      setDateFrom('');
      setDateTo('');
      setMin('');
      setTarget('');
      setReason('');
      setTouched({});
    }
  };

  const dayLabel = (d: string) => `${weekdayShort(d)} ${dayMonth(d)}`;
  const errorText = (msg?: string) =>
    msg ? <p className="mt-1 text-xs text-red-600 dark:text-red-400">{msg}</p> : null;
  const invalidClass = (msg?: string) => (msg ? 'border-red-500 focus-visible:ring-red-500' : '');

  return (
    <form onSubmit={handleSubmit} className="space-y-4 rounded-md border bg-muted/30 p-4" data-testid="plan-constraint-form">
      <div>
        <Label htmlFor="constraint-type">Rodzaj wyjątku</Label>
        <select
          id="constraint-type"
          value={type}
          onChange={(e) => changeType(e.target.value as PlanConstraintType)}
          className={cn(SELECT_CLASS, 'mt-1')}
          disabled={busy}
        >
          {(Object.keys(CONSTRAINT_TYPE_LABELS) as PlanConstraintType[]).map((t) => (
            <option key={t} value={t}>
              {CONSTRAINT_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-muted-foreground">{TYPE_HINTS[type]}</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {isEmployeeType ? (
          <div>
            <Label htmlFor="constraint-employee">Pracownik</Label>
            <div className="mt-1">
              <SearchableSelect
                id="constraint-employee"
                options={employeeOptions}
                value={employeeId}
                onChange={(v) => {
                  setEmployeeId(v);
                  setSlotId('');
                  touch('employee');
                }}
                placeholder="Wybierz pracownika"
                searchPlaceholder="Szukaj po nazwisku lub imieniu"
                emptyMessage="Nikt nie pasuje"
                disabled={busy}
              />
            </div>
            {errorText(shown('employee', !employeeId))}
          </div>
        ) : (
          <div>
            <Label htmlFor="constraint-position">Stanowisko</Label>
            <div className="mt-1">
              <SearchableSelect
                id="constraint-position"
                options={positions.map((p) => ({ value: p.id, label: p.label }))}
                value={positionId}
                onChange={(v) => {
                  setPositionId(v);
                  setSlotId('');
                  touch('position');
                }}
                placeholder="Wybierz stanowisko"
                searchPlaceholder="Szukaj stanowiska"
                emptyMessage="Nic nie pasuje"
                disabled={busy}
              />
            </div>
            {errorText(shown('position', !positionId))}
          </div>
        )}

        {needsSlot && (
          <div>
            <Label htmlFor="constraint-slot">Zmiana</Label>
            <select
              id="constraint-slot"
              value={slotId}
              onChange={(e) => setSlotId(e.target.value)}
              onBlur={() => touch('slot')}
              disabled={busy || slotChoices.length === 0}
              className={cn(SELECT_CLASS, 'mt-1', invalidClass(shown('slot', !slotId)))}
            >
              <option value="">
                {slotChoices.length === 0
                  ? isEmployeeType
                    ? 'Najpierw wybierz pracownika'
                    : 'Najpierw wybierz stanowisko'
                  : 'Wybierz zmianę'}
              </option>
              {slotChoices.map((s) => (
                <option key={s.id} value={s.id}>
                  {slotHours(s.startTime, s.endTime)} ({s.shiftName})
                </option>
              ))}
            </select>
            {errorText(shown('slot', !slotId))}
          </div>
        )}

        <div>
          <Label htmlFor="constraint-date-from">{singleDay ? 'Dzień' : 'Od dnia'}</Label>
          <select
            id="constraint-date-from"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            onBlur={() => touch('dateFrom')}
            disabled={busy}
            className={cn(SELECT_CLASS, 'mt-1', invalidClass(shown('dateFrom', !dateFrom)))}
          >
            <option value="">Wybierz dzień</option>
            {board.dates.map((d) => (
              <option key={d} value={d}>
                {dayLabel(d)}
              </option>
            ))}
          </select>
          {errorText(shown('dateFrom', !dateFrom))}
        </div>

        {!singleDay && (
          <div>
            <Label htmlFor="constraint-date-to">Do dnia</Label>
            <select
              id="constraint-date-to"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              disabled={busy}
              className={cn(SELECT_CLASS, 'mt-1', invalidClass(errors.dateTo))}
            >
              <option value="">Tylko jeden dzień</option>
              {board.dates.map((d) => (
                <option key={d} value={d}>
                  {dayLabel(d)}
                </option>
              ))}
            </select>
            {errorText(errors.dateTo)}
          </div>
        )}

        {singleDay && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="constraint-min">Minimum osób</Label>
              <Input
                id="constraint-min"
                inputMode="numeric"
                value={min}
                onChange={(e) => setMin(e.target.value)}
                onBlur={() => touch('min')}
                disabled={busy}
                className={cn('mt-1', invalidClass(shown('min', min.trim() === '')))}
              />
              {errorText(shown('min', min.trim() === ''))}
            </div>
            <div>
              <Label htmlFor="constraint-target">Docelowo osób</Label>
              <Input
                id="constraint-target"
                inputMode="numeric"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                onBlur={() => touch('target')}
                disabled={busy}
                className={cn('mt-1', invalidClass(shown('target', target.trim() === '')))}
              />
              {errorText(shown('target', target.trim() === ''))}
            </div>
          </div>
        )}
      </div>

      <div>
        <Label htmlFor="constraint-reason">Powód (opcjonalnie)</Label>
        <Textarea
          id="constraint-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          disabled={busy}
          placeholder="np. szkolenie, wizyta u lekarza"
          className={cn('mt-1', invalidClass(errors.reason))}
        />
        <div className="flex justify-between text-xs">
          <span className="text-red-600 dark:text-red-400">{errors.reason}</span>
          <span className={cn('text-muted-foreground', errors.reason && 'text-red-600 dark:text-red-400')}>
            {reason.trim().length}/{REASON_MAX_LENGTH}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        {invalid && (
          <span className="mr-auto text-xs text-muted-foreground" data-testid="plan-constraint-missing">
            Aby dodać wyjątek: {Object.values(errors).filter(Boolean)[0]?.toLowerCase()}.
          </span>
        )}
        <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
          Anuluj
        </Button>
        <Button type="submit" disabled={invalid || busy} data-testid="plan-constraint-submit">
          {busy ? 'Zapisywanie...' : 'Dodaj wyjątek'}
        </Button>
      </div>
    </form>
  );
};
